import { Tabs, Text, Tooltip } from "@shopify/polaris"
import classNames from "classnames"
import { observer } from "mobx-react"
import numeral from "numeral"
import React from "react"

import { SimpleTableList, SpinnerContainer } from "@/components"
import CasingPly from "@/components/CasingPly/CasingPly"
import DynamicLoadError from "@/components/DynamicLoadError/DynamicLoadError"
import STableStyles from "@/components/SimpleTableList/index.module.scss"
import { SimpleTableListFields } from "@/components/SimpleTableList/types"
import stores from "@/stores"
import { autoReplenishPoint } from "@/utils/formatNumber"

import { ArrowDiagonalIcon, ArrowDownIcon } from "./Icon"
import styles from "./index.module.scss"

const DashboardLineChart = React.lazy(
  () => import("@/components/Charts/DashboardLineChart"),
)

// 声明全局 t 函数类型
declare global {
  interface Window {
    t: (key: string) => string
  }
}

const t = (key: string) => window.t(key)

interface State {
  tabs: Array<{
    id: string
    content: string
    panelID: string
  }>
  selected: number
}

@observer
class ViewsSubscribersCharts extends React.Component<any, State> {
  constructor(props: any) {
    super(props)

    this.state = {
      tabs: [
        {
          id: "returns-requests",
          content: t("ReturnsRequests"),
          panelID: "returnsRequests",
        },
        {
          id: "returns-rate",
          content: t("ReturnsRate"),
          panelID: "returnsRate",
        },
      ],
      selected: 0,
    }
  }

  public componentDidMount() {}

  handleTabChange = (index: number) => {
    this.setState({ selected: index })
  }

  // 展示统计数据
  showStatisticsCount = (selected: number, indexVal: string) => {
    // 直接从 stores 获取最新数据，确保实时更新
    const {
      ReturnRequestsDataStore: {
        requests_count,
        total_order_product_count,
        total_order_return_count,
        total_return_rate,
        compare_rate,
        hasCompare,
      },
    } = stores

    const countMap = {
      0: requests_count,
      1: total_return_rate,
    }

    return (
      <div className="flex items-center gap-2">
        <span
          style={
            { "--p-text-body-sm-font-weight": "600" } as React.CSSProperties
          }
        >
          <Text variant="bodyMd" as="span">
            <span className="text-[#303030]">
              {selected === 0
                ? numeral(countMap[selected]).format("0,0")
                : selected === 1
                  ? total_order_product_count > 0
                    ? `${countMap[selected] > 0 ? countMap[selected] : "0.00"}%  (${total_order_return_count} / ${total_order_product_count})`
                    : "0.00%"
                  : null}
            </span>
          </Text>
        </span>
        {compare_rate && compare_rate[indexVal] !== 0 && hasCompare ? (
          <div className="flex items-center gap-1">
            {compare_rate[indexVal] > 0 ? (
              <ArrowDiagonalIcon />
            ) : (
              <ArrowDownIcon />
            )}
            {compare_rate[indexVal] > 0 ? (
              <Text as="span" variant="bodyXs" tone="subdued">
                {Math.round(compare_rate[indexVal])}%
              </Text>
            ) : (
              <Text as="span" variant="bodyXs" tone="subdued">
                {Math.round(compare_rate[indexVal])
                  ?.toString()
                  ?.replace(/^-/, "")}
                %
              </Text>
            )}
          </div>
        ) : null}
      </div>
    )
  }

  render() {
    const { ReturnRequestsDataStore } = stores
    const { tabs, selected } = this.state

    const loading = stores.ReturnRequestsDataStore.loading.overviewLoading

    // 动态生成带有统计数据的 tab 内容
    const tabsWithContent = tabs.map((tab, index) => ({
      ...tab,
      content: (
        <div className="flex flex-col items-start gap-2">
          <Tooltip
            content={
              index === 0
                ? t("TheTotalNumberOfReturnRequestsCrea")
                : t("ThePercentageOfItemsReturnedToYour")
            }
            preferredPosition="above"
          >
            <span
              style={{
                borderBottom: "0.125rem dotted var(--p-color-border-tertiary)",
                paddingBottom: "2px",
                "--p-text-heading-sm-font-weight": "600",
              }}
              className="text-[#303030]"
            >
              <Text as="span" variant="headingSm">
                {index === 0 ? t("ReturnsRequests") : t("ReturnsRate")}
              </Text>
            </span>
          </Tooltip>
          {this.showStatisticsCount(
            index,
            index === 0 ? "request_count" : "return_rate",
          )}
        </div>
      ),
    })) as any // 使用类型断言绕过 Polaris Tabs 的类型限制

    return (
      <div
        className={classNames("Polaris-Card", styles.AnalysisContainer)}
        style={
          {
            "--p-action-primary": "#84CAE9",
            "--p-surface-primary-selected-pressed": "#999EA4",
          } as React.CSSProperties
        }
      >
        <Tabs
          tabs={tabsWithContent}
          selected={selected}
          onSelect={this.handleTabChange}
        >
          <CasingPly show={loading}>{this.dataTable()}</CasingPly>
          <SpinnerContainer
            loading={loading}
            spinnerClassName={["relative", "-top-20"]}
          >
            <DynamicLoadError errorMsg={t("NetworkErrorUnableToLoadCharts")}>
              <React.Suspense
                fallback={
                  <SpinnerContainer loading={!loading}>
                    <div className={styles.CardSection__ReactECharts} />
                  </SpinnerContainer>
                }
              >
                <DashboardLineChart
                  className={styles.CardSection__ReactECharts}
                  option={
                    ReturnRequestsDataStore?.chartOptions[
                      tabs[selected].panelID
                    ]
                  }
                />
              </React.Suspense>
            </DynamicLoadError>
          </SpinnerContainer>
        </Tabs>
      </div>
    )
  }

  dataTable() {
    const fields: SimpleTableListFields[] = [
        { field: "title", title: "title" },
        { className: STableStyles.AlignRight, field: "val", title: "Val" },
      ],
      data: any[] = [
        // {
        //   title: "Conversion rate",
        //   val: conversion_rate === "0%" ? "-" : conversion_rate
        // }
      ]

    return <SimpleTableList data={data} fields={fields} noHead />
  }
}

export default observer(ViewsSubscribersCharts)
